import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import Logo from "./Logo";
import { WHATSAPP_DEFAULT } from "./shared";

const LINKS = [
    { href: "#sobre", label: "Sobre" },
    { href: "#servicos", label: "Serviços" },
    { href: "#livro", label: "Livro" },
    { href: "#faq", label: "Dúvidas" },
    { href: "#contato", label: "Contato" },
];

const HeaderNav = ({ base = "" }) => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
                scrolled ? "bg-cream/85 backdrop-blur-md border-b border-lav shadow-[0_8px_30px_rgba(93,74,122,0.06)]" : "bg-transparent"
            }`}
            data-testid="header-nav"
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
                <a href={`${base}#inicio`} className="flex items-center gap-3" data-testid="nav-logo-link" onClick={() => setOpen(false)}>
                    <Logo size={42} />
                    <span className="leading-tight">
                        <span className="block font-serif text-lg font-bold text-plum">Maricleia Araújo</span>
                        <span className="block text-[11px] uppercase tracking-[0.22em] text-serenity">Gerontóloga</span>
                    </span>
                </a>

                <nav className="hidden items-center gap-8 lg:flex" aria-label="Navegação principal">
                    {LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={`${base}${l.href}`}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className="text-sm font-medium text-ink transition-colors duration-300 hover:text-plum"
                        >
                            {l.label}
                        </a>
                    ))}
                    <a
                        href={WHATSAPP_DEFAULT}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-whatsapp-button"
                        className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-lilac via-serenity to-serenitylight px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-serenity/30 transition-transform duration-300 hover:scale-[1.03]"
                    >
                        <MessageCircle size={17} />
                        Agendar pelo WhatsApp
                    </a>
                </nav>

                <button
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-lav bg-white/70 text-plum lg:hidden"
                    onClick={() => setOpen((v) => !v)}
                    aria-label={open ? "Fechar menu" : "Abrir menu"}
                    data-testid="nav-mobile-toggle"
                >
                    {open ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                        className="overflow-hidden border-b border-lav bg-cream/95 backdrop-blur-md lg:hidden"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="flex flex-col gap-1 px-6 py-4">
                            {LINKS.map((l) => (
                                <a
                                    key={l.href}
                                    href={`${base}${l.href}`}
                                    onClick={() => setOpen(false)}
                                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                    className="rounded-xl px-3 py-3 text-base font-medium text-ink hover:bg-lav/50"
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href={WHATSAPP_DEFAULT}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="nav-mobile-whatsapp-button"
                                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-lilac via-serenity to-serenitylight px-6 py-3.5 text-base font-semibold text-white"
                            >
                                <MessageCircle size={18} />
                                Agendar pelo WhatsApp
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </motion.header>
    );
};

export default HeaderNav;
